import { ButtonFlavor, ButtonType, ExButton, ExMenuItem, ExSelect } from '@boomi/exosphere';
import { useEffect, useState } from 'react';
import { grantTypeOptions } from '../../../constants/oauth';
import { getOpenApiSchemaInfo, SaveOpenApiSchemaConfig } from '../../../sources/service';
import translations from '../../../translations';
import type { OAuth2GrantType } from '../../../types/identityProvider';
import type { OpenApiPathInfo, ServiceElementResponseAPI } from '../../../types/service';
import { isNullOrEmpty } from '../../../utils/guard';
import Breadcrumb from '../../Breadcrumb';
import FormGroup from '../../generic/FormGroup';
import Table from '../../generic/Table';
import Toggle from '../../inputs/Toggle';
import Loader from '../../loader/Loader';
import { useNotificationDispatch } from '../../notifications/NotificationProvider';
import { useOpenApiConnector } from '../contextProviders/OpenApiConnectorProvider';
import { useServices } from '../contextProviders/ServicesProvider';
import type { ServiceViews } from '../ServiceConstants';
import { OpenApiTableConfigModal } from './OpenApiTableConfiguration';
import { OpenApiTypeInput } from './OpenApiTypeInput';

interface OpenApiConfigurationProps {
    currentServiceData: ServiceElementResponseAPI | null | undefined;
    switchView: (view: ServiceViews) => void;
}

const OpenApiConfiguration = ({ currentServiceData, switchView }: OpenApiConfigurationProps) => {
    const [isInfoLoading, setIsInfoLoading] = useState<boolean>(false);
    const [showTableModal, setShowTableModal] = useState<boolean>(false);
    const { servicesState, setServicesState } = useServices();
    const schemaValueId =
        servicesState?.selectedValues?.find((x) => x.name === 'Schema Value')?.value?.id ??
        currentServiceData?.configurationValues?.find((x) => x.developerName === 'Schema Value')
            ?.valueElementToReferenceId?.id ??
        servicesState?.selectedValues?.find((x) => x.name === 'Schema URL')?.value?.id ??
        currentServiceData?.configurationValues?.find((x) => x.developerName === 'Schema URL')
            ?.valueElementToReferenceId?.id;

    const {
        hasSubmitted,
        setHasSubmitted,
        schemaInfo,
        setSchemaInfo,
        onSecurityChange,
        onUserInfoUrlChange,
        setOpenApiAPI,
        openApiAPI,
        onFirstNameChange,
        onLastNameChange,
        onEmailChange,
        onUserIdChange,
        onUsernameChange,
        onScopesChange,
        isSecurityValid,
        setEditingPath,
        onGrantTypeChange,
    } = useOpenApiConnector();
    const { breadcrumbs } = useServices();
    const isBasic = schemaInfo?.security === 'BASIC';
    const isOIDC = schemaInfo?.security === 'OIDC';
    const isOAuth2 = schemaInfo?.security === 'OAUTH2';
    const shouldConfigureMe =
        (isOAuth2 && (!schemaInfo?.grantType || schemaInfo.grantType === 'AuthorizationCode')) ||
        isBasic;
    const scopesRequired =
        !isBasic &&
        isOIDC &&
        (!schemaInfo?.grantType || schemaInfo.grantType === 'AuthorizationCode');

    const dispatchNotification = useNotificationDispatch();

    const onItemChange = (info: OpenApiPathInfo, rowIndex: number) => {
        if (!schemaInfo) {
            return;
        }
        const schema = { ...schemaInfo };
        schema.paths[rowIndex] = info;
        setSchemaInfo(schema);
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies: Requires dedicated refactor to include all dependencies
    useEffect(() => {
        const getSchemaInfo = async () => {
            setIsInfoLoading(true);
            try {
                if (!schemaValueId) {
                    dispatchNotification({
                        type: 'notifyError',
                        payload: {
                            error: 'An OpenApi schema value must be set',
                        },
                    });
                    goBack();
                    return;
                }
                const openApiAPI = await getOpenApiSchemaInfo({
                    schemaValueId,
                    serviceId: currentServiceData?.id,
                    openApiId: currentServiceData?.id ? null : servicesState?.openApiId,
                });
                if (openApiAPI) {
                    setOpenApiAPI(openApiAPI);
                    setSchemaInfo(openApiAPI?.schemaInfo);
                }
            } catch (error) {
                dispatchNotification({
                    type: 'notifyError',
                    payload: {
                        error,
                    },
                });
                setIsInfoLoading(false);
                setOpenApiAPI(null);
                setSchemaInfo(null);
            }
            setIsInfoLoading(false);
        };
        getSchemaInfo();
    }, [dispatchNotification, schemaValueId, setOpenApiAPI, setSchemaInfo]);

    const goBack = () => switchView(breadcrumbs.trail[breadcrumbs.trail.length - 2].id);

    const saveSchemaConfiguration = async () => {
        try {
            if (!schemaInfo) {
                dispatchNotification({
                    type: 'notifyError',
                    payload: {
                        error: translations.OPENAPI_TABLE_empty_schema_error,
                    },
                });
                return;
            }
            if (schemaInfo.paths.filter((x) => x.shouldGenerateType).length === 0) {
                dispatchNotification({
                    type: 'notifyError',
                    payload: {
                        error: translations.OPENAPI_TABLE_no_types_error,
                    },
                });
                return;
            }
            setHasSubmitted(true);
            const securityValidationResult = isSecurityValid();
            if (openApiAPI && schemaInfo && securityValidationResult.isValid) {
                const openApiAPIToSave = { ...openApiAPI, schemaInfo: schemaInfo };
                if (isNullOrEmpty(schemaInfo.security)) {
                    openApiAPIToSave.schemaInfo.security = 'NONE';
                }
                const savedOpenApi = await SaveOpenApiSchemaConfig(openApiAPIToSave);
                setServicesState((previousState) => ({
                    ...previousState,
                    openApiId: savedOpenApi?.id ?? previousState.openApiId ?? null,
                }));
                dispatchNotification({
                    type: 'notifySuccess',
                    payload: { message: translations.OPENAPI_validation_success },
                });

                goBack();
            } else {
                dispatchNotification({
                    type: 'notifyError',
                    payload: {
                        error: securityValidationResult.validationMessage
                            ? securityValidationResult.validationMessage
                            : translations.OPENAPI_validation_fail,
                    },
                });
            }
        } catch (error) {
            dispatchNotification({
                type: 'notifyError',
                payload: {
                    error,
                },
            });
        }
    };

    if (isInfoLoading) {
        return <Loader />;
    }

    const openApiAuthOptions: { label: string; value: string }[] = [
        { label: translations.OPENAPI_security_none, value: 'NONE' },
        { label: translations.OPENAPI_security_oauth, value: 'OAUTH2' },
        { label: translations.OPENAPI_security_oidc, value: 'OIDC' },
        { label: translations.OPENAPI_security_basic, value: 'BASIC' },
    ];

    return (
        <div className="full-height flex-column">
            <div className="admin-page">
                <div className="margin-bottom" data-testid="connector-breadcrumbs">
                    <Breadcrumb trail={breadcrumbs.trail} activeItemId={breadcrumbs.activeItemId} />
                </div>
                {schemaInfo && (
                    <>
                        <h1>{translations.OPENAPI_security}</h1>
                        <FormGroup label={translations.OPENAPI_security} htmlFor="security-select">
                            <ExSelect
                                id="security-select"
                                hideClearIcon
                                selected={
                                    openApiAuthOptions.find((x) => x.value === schemaInfo.security)
                                        ?.label ?? openApiAuthOptions[0].label
                                }
                                onChange={(e) => onSecurityChange(e.detail.value)}
                                required
                                className="form-control-width"
                                data-testId="security-select"
                            >
                                {openApiAuthOptions.map((item) => (
                                    <ExMenuItem key={item.value} value={item.value}>
                                        {item.label}
                                    </ExMenuItem>
                                ))}
                            </ExSelect>
                        </FormGroup>
                        {(isOAuth2 || isOIDC) && (
                            <FormGroup>
                                <ExSelect
                                    id="oauth-grant-type"
                                    label={translations.OPENAPI_security_oauth_type}
                                    selected={
                                        grantTypeOptions.find(
                                            (x) => x.value === schemaInfo.grantType,
                                        )?.label ?? grantTypeOptions[0].label
                                    }
                                    onChange={(e) =>
                                        onGrantTypeChange(e.detail.value as OAuth2GrantType)
                                    }
                                    hideClearIcon
                                    className="form-control-width"
                                    data-testId="oauth-grant-type"
                                >
                                    {grantTypeOptions.map((item) => (
                                        <ExMenuItem key={item.value} value={item.value}>
                                            {item.label}
                                        </ExMenuItem>
                                    ))}
                                </ExSelect>
                            </FormGroup>
                        )}
                        {shouldConfigureMe && (
                            <>
                                <FormGroup
                                    label={translations.OPENAPI_security_userinfo}
                                    isRequired
                                    showValidation={hasSubmitted}
                                    htmlFor="openapi-security-userinfo"
                                >
                                    <input
                                        id="openapi-security-userinfo"
                                        value={schemaInfo?.me?.userInfoUrl ?? ''}
                                        onChange={onUserInfoUrlChange}
                                        required={!isBasic}
                                        className="form-control form-control-width"
                                        type="text"
                                    />
                                </FormGroup>
                                <FormGroup
                                    label={translations.OPENAPI_security_firstname}
                                    isRequired={!isBasic}
                                    showValidation={hasSubmitted && !isBasic}
                                    htmlFor="openapi-security-firstName"
                                >
                                    <input
                                        id="openapi-security-firstName"
                                        value={schemaInfo?.me?.firstName ?? ''}
                                        onChange={onFirstNameChange}
                                        required={!isBasic}
                                        className="form-control form-control-width"
                                        type="text"
                                    />
                                </FormGroup>
                                <FormGroup
                                    label={translations.OPENAPI_security_lastname}
                                    isRequired={!isBasic}
                                    showValidation={hasSubmitted && !isBasic}
                                    htmlFor="openapi-security-lastName"
                                >
                                    <input
                                        id="openapi-security-lastName"
                                        value={schemaInfo?.me?.lastName ?? ''}
                                        onChange={onLastNameChange}
                                        required={!isBasic}
                                        className="form-control form-control-width"
                                        type="text"
                                    />
                                </FormGroup>
                                <FormGroup
                                    label={translations.OPENAPI_security_email}
                                    isRequired={!isBasic}
                                    showValidation={hasSubmitted && !isBasic}
                                    htmlFor="openapi-security-email"
                                >
                                    <input
                                        id="openapi-security-email"
                                        value={schemaInfo?.me?.email ?? ''}
                                        onChange={onEmailChange}
                                        required={!isBasic}
                                        className="form-control form-control-width"
                                        type="text"
                                    />
                                </FormGroup>
                                <FormGroup
                                    label={translations.OPENAPI_security_username}
                                    isRequired={!isBasic}
                                    showValidation={hasSubmitted && !isBasic}
                                    htmlFor="openapi-security-username"
                                >
                                    <input
                                        id="openapi-security-username"
                                        value={schemaInfo?.me?.username ?? ''}
                                        onChange={onUsernameChange}
                                        required={!isBasic}
                                        className="form-control form-control-width"
                                        type="text"
                                    />
                                </FormGroup>
                                <FormGroup
                                    label={translations.OPENAPI_security_userId}
                                    isRequired={!isBasic}
                                    showValidation={hasSubmitted && !isBasic}
                                    htmlFor="openapi-security-userId"
                                >
                                    <input
                                        id="openapi-security-userId"
                                        value={schemaInfo?.me?.userId ?? ''}
                                        onChange={onUserIdChange}
                                        required={!isBasic}
                                        className="form-control form-control-width"
                                        type="text"
                                    />
                                </FormGroup>
                            </>
                        )}
                        {isOIDC && (
                            <FormGroup
                                label={translations.OPENAPI_security_scopes}
                                isRequired={scopesRequired}
                                showValidation={hasSubmitted && scopesRequired}
                                htmlFor="openapi-security-scopes"
                            >
                                <textarea
                                    id="openapi-security-scopes"
                                    value={schemaInfo?.scopes ?? ''}
                                    onChange={onScopesChange}
                                    required={scopesRequired}
                                    className="form-control form-control-width"
                                />
                            </FormGroup>
                        )}
                        <h1>{translations.OPENAPI_schema}</h1>
                        <h2>{schemaInfo.serverUrl}</h2>
                        <Table<OpenApiPathInfo>
                            wrapperClassName="margin-top"
                            columns={[
                                {
                                    renderHeader: () => translations.OPENAPI_TABLE_create_type,
                                    renderCell: ({ item, rowIndex }) => (
                                        <Toggle
                                            id={`should-create-type-${rowIndex}`}
                                            isOn={item.shouldGenerateType}
                                            onChange={({ isOn }) =>
                                                onItemChange(
                                                    { ...item, shouldGenerateType: isOn },
                                                    rowIndex,
                                                )
                                            }
                                            testId={`should-create-type-${rowIndex}`}
                                        />
                                    ),
                                    size: '7rem',
                                },
                                {
                                    renderHeader: () => translations.OPENAPI_TABLE_path,
                                    renderCell: ({ item }) => item.path,
                                },
                                {
                                    renderHeader: () =>
                                        translations.OPENAPI_TABLE_request_object_name,
                                    renderCell: ({ item, rowIndex }) => (
                                        <OpenApiTypeInput
                                            pathInfo={item}
                                            request={true}
                                            onItemChange={onItemChange}
                                            rowIndex={rowIndex}
                                        />
                                    ),
                                },
                                {
                                    renderHeader: () =>
                                        translations.OPENAPI_TABLE_response_object_name,
                                    renderCell: ({ item, rowIndex }) => (
                                        <OpenApiTypeInput
                                            pathInfo={item}
                                            request={false}
                                            onItemChange={onItemChange}
                                            rowIndex={rowIndex}
                                        />
                                    ),
                                },
                                {
                                    renderHeader: () => translations.OPENAPI_TABLE_operation,
                                    renderCell: ({ item }) => item.operation.method,
                                },
                                {
                                    renderHeader: () => translations.OPENAPI_TABLE_description,
                                    renderCell: ({ item }) => item.responseSchema?.description,
                                },
                                {
                                    renderHeader: () => translations.COMMON_TABLE_actions,
                                    renderCell: ({ item, rowIndex }) => {
                                        if (!item.responseSchema) {
                                            return null;
                                        }
                                        return (
                                            <ExButton
                                                title={translations.COMMON_back}
                                                onClick={() => {
                                                    setEditingPath({
                                                        index: rowIndex,
                                                        path: item,
                                                    });
                                                    setShowTableModal(true);
                                                }}
                                                flavor={ButtonFlavor.BRANDED}
                                                type={ButtonType.PRIMARY}
                                            >
                                                {translations.OPENAPI_TABLE_configure_table}
                                            </ExButton>
                                        );
                                    },
                                },
                            ]}
                            items={schemaInfo.paths}
                            isLoading={isInfoLoading}
                        />
                    </>
                )}
            </div>
            <OpenApiTableConfigModal show={showTableModal} setShow={setShowTableModal} />
            <div className="admin-footer">
                <button type="button" className="btn btn-default outcome" onClick={goBack}>
                    {'Back'}
                </button>
                <button
                    type="button"
                    className="btn btn-primary outcome"
                    onClick={saveSchemaConfiguration}
                >
                    {'Save'}
                </button>
            </div>
        </div>
    );
};

export default OpenApiConfiguration;
